import { useRef, useState } from "react";
import { useAuth } from "../lib/auth";

// Shared owner-desk UI: the confirm-before-send document uploader (F34/F35/F38 + the F27-hardened
// surfacing) and the drag-safe backdrop close (F25). Used by both the building file's "Add a document"
// and step 4 of Add a building, so a document lands the same way from either entry.

export function fmtBytes(n) {
  if (n == null) return "";
  if (n >= 1024 * 1024) return (n / (1024 * 1024)).toFixed(1) + " MB";
  if (n >= 1024) return Math.round(n / 1024) + " KB";
  return n + " B";
}

// Close a scrim ONLY when a click both STARTS and ENDS on the backdrop itself. A drag-select that
// begins inside the panel and releases on the backdrop must never close it (F25 — no lost input).
export function useBackdropClose(onClose) {
  const downOnScrim = useRef(false);
  return {
    onMouseDown: (e) => { downOnScrim.current = e.target === e.currentTarget; },
    onClick: (e) => { if (downOnScrim.current && e.target === e.currentTarget) onClose(); },
  };
}

// Confirm before upload (F34): choosing a file shows its name + size with Upload/Cancel; only Upload
// sends — documents are append-only, so a wrong file is permanent. Visible progress (F35): "Uploading
// …" then "Received". The owner never waits (F38): after Upload we say it's safe to leave.
export function DocUploader({ buildingRef, onFiled, testidPrefix = "doc" }) {
  const { authedFetch } = useAuth();
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState("idle");   // idle | chosen | uploading | received | failed
  const [note, setNote] = useState("");
  const [duplicate, setDuplicate] = useState(false);

  const choose = (f) => { if (!f) return; setFile(f); setPhase("chosen"); setNote(""); };
  const cancel = () => { setFile(null); setPhase("idle"); setNote(""); };

  const send = async () => {
    if (!file) return;
    setPhase("uploading"); setNote("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const data = await authedFetch(`/owner/commands/building/${buildingRef}/upload`,
        { method: "POST", body: fd });
      if (data && data.duplicate) {
        // F41/F78: a duplicate on THIS building — nothing new to read, and neutral, not a failure. The
        // picker STAYS (does not disappear) so the owner can pick a different file right away.
        setPhase("received");
        setDuplicate(true);
        setNote(data.message || "You already sent me this file. Nothing new to read.");
        // F41/F53: the SAME bytes on a DIFFERENT building opens a staff handoff on this one — refresh
        // so the desk picks up the new "with our team" state right away.
        if (data.cross_building) onFiled && onFiled();
      } else if (data && data.filed) {
        setPhase("received");
        setDuplicate(false);
        setNote("Received. I'll let you know when it's read, and you can leave this page.");
        onFiled && onFiled();
      } else {
        setPhase("failed"); setNote("That upload didn't reach us. Try again.");
      }
    } catch (e) {
      setPhase("failed");
      if (!e.status || e.status === 401) setNote("That upload didn't reach us. Try again.");
      else setNote(e.detail || "That upload didn't reach us. Try again.");
    }
  };

  return (
    <div className="doc-uploader" data-testid={testidPrefix + "-uploader"}>
      {(phase === "idle" || phase === "failed" || (phase === "received" && duplicate)) && (
        <input type="file" className="set-input" accept=".pdf,.xlsx"
               data-testid={testidPrefix + "-file"} onChange={(e) => choose(e.target.files[0])} />
      )}
      {phase === "chosen" && (
        <div className="doc-confirm" data-testid={testidPrefix + "-confirm-box"}>
          <div className="doc-chosen" data-testid={testidPrefix + "-chosen"}>
            {file.name} <span className="doc-size">· {fmtBytes(file.size)}</span>
          </div>
          <p className="doc-confirm-note">I keep documents exactly as filed — a wrong one can't be
            undone. Send this?</p>
          <div className="doc-confirm-actions">
            <button className="mlog-primary" data-testid={testidPrefix + "-upload"} onClick={send}>Upload</button>
            <button className="chrome-btn" data-testid={testidPrefix + "-cancel"} onClick={cancel}>Cancel</button>
          </div>
        </div>
      )}
      {phase === "uploading" && (
        <div className="doc-status" data-testid={testidPrefix + "-status"}>
          Uploading {file.name}… <span className="doc-size">{fmtBytes(file.size)}</span>
        </div>
      )}
      {note && (
        <div className={"doc-note" + (phase === "failed" ? " doc-note-error"
                                      : phase === "received" ? " doc-note-done" : "")}
             role={phase === "failed" ? "alert" : undefined} data-testid={testidPrefix + "-note"}>
          {note}
        </div>
      )}
    </div>
  );
}
